import { useState, useEffect } from "react";
import { LocalizationProvider, DatePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import { format, isValid, parse } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Autocomplete,
} from "@mui/material";
import { Search } from "@mui/icons-material";

import Colors from "../../../constants/Colors";

interface Props {
  inicialEmissionError: boolean;
  finalEmissionError: boolean;
  toggleInicialEmissionError: (errorValue: boolean) => void;
  toggleFinalEmissionError: (errorValue: boolean) => void;
  visitSearchParams: any;
  setVisitSearchParams: (params: any) => void;
}

const visitTypesMock = [
  { id: "0", nome: "Reunião" },
  { id: "1", nome: "Venda" },
  { id: "2", nome: "Cliente Novo" },
  { id: "3", nome: "Fechou" },
  { id: "4", nome: "Mudou de Ramo" },
  { id: "5", nome: "Outros" },
];

const situacaoMock = [
  { id: "1", nome: "Aberta" },
  { id: "2", nome: "Encerrada" },
];

const VisitSearchCard = (props: Props) => {
  const {
    finalEmissionError,
    inicialEmissionError,
    toggleFinalEmissionError,
    toggleInicialEmissionError,
    visitSearchParams,
    setVisitSearchParams,
  } = props;

  const [inicialEmissionDate, setInicialEmissionDate] = useState<Date | null>(() => {
    if (visitSearchParams.emissaoInicial) {
      const d = parse(visitSearchParams.emissaoInicial, "dd/MM/yyyy", new Date());
      d.setHours(12, 0, 0, 0);
      return d;
    }
    return null;
  });
  const [finalEmissionDate, setFinalEmissionDate] = useState<Date | null>(() => {
    if (visitSearchParams.emissaoFinal) {
      const d = parse(visitSearchParams.emissaoFinal, "dd/MM/yyyy", new Date());
      d.setHours(12, 0, 0, 0);
      return d;
    }
    return null;
  });

  const [inicialEmissionErrorMessage, setInicialEmissionErrorMessage] =
    useState("");
  const [finalEmissionErrorMessage, setFinalEmissionErrorMessage] =
    useState("");

  const updateParams = (updates: any) => {
    setVisitSearchParams((prev: any) => ({ ...prev, ...updates }));
  };

  const handleInicialEmissionDateChange = (date: Date | null): void => {
    let formattedDate = "";
    if (!date || !isValid(date)) {
      if (!inicialEmissionError) toggleInicialEmissionError(true);
      setInicialEmissionErrorMessage("A data informada é inválida");
      return;
    } else if (finalEmissionDate && finalEmissionDate < date) {
      if (!inicialEmissionError) toggleInicialEmissionError(true);
      setInicialEmissionErrorMessage(
        "A data inicial deve ser menor que a data final",
      );
      return;
    } else {
      if (inicialEmissionError) toggleInicialEmissionError(false);
      // Use local components to avoid unintended shifts, keeping midday for safety
      const normalizedDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        12,
        0,
        0,
      );
      formattedDate = format(normalizedDate, "dd/MM/yyyy");
      setInicialEmissionDate(normalizedDate);
      setInicialEmissionErrorMessage("");
      updateParams({ emissaoInicial: formattedDate });
    }
  };

  const handleFinalEmissionDateChange = (date: Date | null): void => {
    let formattedDate = "";
    if (!date || !isValid(date)) {
      if (!finalEmissionError) toggleFinalEmissionError(true);
      setFinalEmissionErrorMessage("A data informada é inválida");
      return;
    } else if (inicialEmissionDate && date < inicialEmissionDate) {
      if (!finalEmissionError) toggleFinalEmissionError(true);
      setFinalEmissionErrorMessage(
        "A data inicial deve ser menor que a data final",
      );
      return;
    } else {
      if (finalEmissionError) toggleFinalEmissionError(false);
      // Use local components to avoid unintended shifts, keeping midday for safety
      const normalizedDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        12,
        0,
        0,
      );
      formattedDate = format(normalizedDate, "dd/MM/yyyy");
      setFinalEmissionDate(normalizedDate);
      setFinalEmissionErrorMessage("");
      updateParams({ emissaoFinal: formattedDate });
    }
  };

  const handleTypeChange = (selected: any[]) => {
    const ids = selected.map((item) => item.id);
    updateParams({ tipoVisita: ids });
  };

  const handleSituacaoChange = (selected: any[]) => {
    const ids = selected.map((item) => item.id);
    updateParams({ situacaoId: ids });
  };



  return (
    <Grid item xs={12} md={12}>
      <Card>
        <CardContent>
          <Grid container spacing={1}>
            <Grid item xs={12} md={12}>
              <Typography
                sx={{
                  backgroundColor: Colors.titleRowWithBackgroundColor,
                  pl: 1,
                  pt: 0.75,
                  mt: -1,
                }}
                variant="body2"
                color={Colors.titleWithBackgroudColor}
              >
                <Search fontSize={"inherit"} /> Filtros
              </Typography>
            </Grid>

            <Grid item xs={12} md={12}>
              <TextField
                fullWidth
                label={"Cliente"}
                value={visitSearchParams.cliente || ""}
                size={"small"}
                onChange={(ev) => updateParams({ cliente: ev.target.value })}
                style={{ marginTop: 5 }}
              />
            </Grid>

            <Grid item xs={6} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns} locale={ptBR}>
                <DatePicker
                  label={"Emissão Inicial"}
                  value={inicialEmissionDate}
                  onChange={(value) => handleInicialEmissionDateChange(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      helperText={inicialEmissionErrorMessage}
                      error={inicialEmissionError}
                      color="primary"
                      inputProps={{
                        ...params.inputProps,
                        placeholder: "dd/mm/aaaa",
                      }}
                      style={{ width: "100%", marginTop: 5 }}
                    />
                  )}
                  InputProps={{
                    size: "small",
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={6} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns} locale={ptBR}>
                <DatePicker
                  label={"Emissão Final"}
                  value={finalEmissionDate}
                  onChange={(value) => handleFinalEmissionDateChange(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      helperText={finalEmissionErrorMessage}
                      error={finalEmissionError}
                      color="primary"
                      inputProps={{
                        ...params.inputProps,
                        placeholder: "dd/mm/aaaa",
                      }}
                      style={{ width: "100%", marginTop: 5 }}
                    />
                  )}
                  InputProps={{
                    size: "small",
                  }}
                />
              </LocalizationProvider>
            </Grid>

            {/* <Grid item xs={12} md={12}>
              <Autocomplete
                size="small"
                multiple
                id="situacoes"
                options={SITUATIONS}
                getOptionLabel={(option) => option.title}
                filterSelectedOptions
                value={selectedSituations}
                onChange={(ev, value) => handleSituationSelection(ev, value)}
                noOptionsText={"Sem opções"}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="Situação"
                    placeholder="Selecione"
                  />
                )}
              />
            </Grid> */}

            <Grid item xs={12} md={12}>
              <Autocomplete
                size="small"
                multiple
                id="tipoVisita"
                options={visitTypesMock}
                getOptionLabel={(option) => option.nome}
                filterSelectedOptions
                value={visitTypesMock.filter((t) =>
                  (visitSearchParams.tipoVisita || []).includes(t.id),
                )}
                onChange={(_, value) => handleTypeChange(value)}
                noOptionsText={"Sem opções"}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="Tipos de Visita"
                    placeholder="Selecione"
                    style={{ marginTop: 5 }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={12}>
              <Autocomplete
                size="small"
                multiple
                id="situacoes"
                options={situacaoMock}
                getOptionLabel={(option) => option.nome}
                filterSelectedOptions
                value={situacaoMock.filter((s) =>
                  (visitSearchParams.situacaoId || []).includes(s.id),
                )}
                onChange={(_, value) => handleSituacaoChange(value)}
                noOptionsText={"Sem opções"}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="Situação"
                    placeholder="Selecione"
                    style={{ marginTop: 5 }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default VisitSearchCard;
